import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  FileText,
  AppWindow,
  Newspaper,
  CheckSquare,
  ExternalLink,
  Lock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { useAuth, API } from '../../App';

// All 9 stages of the SPRING programme (Build Brief §9)
const ALL_STAGES = [
  { id: "stage00", num: "1", routeNum: "00", name: "Assessment", shortName: "Assess" },
  { id: "stage0", num: "2", routeNum: "0", name: "Idea Generation", shortName: "Idea" },
  { id: "stage1", num: "3", routeNum: "1", name: "Validation", shortName: "Validate" },
  { id: "stage2", num: "4", routeNum: "2", name: "Business Setup", shortName: "Setup" },
  { id: "stage3", num: "5", routeNum: "3", name: "Asset Foundation", shortName: "Assets" },
  { id: "stage4", num: "6", routeNum: "4", name: "Fundraising", shortName: "Fund" },
  { id: "stage5", num: "7", routeNum: "5", name: "Go to Market", shortName: "Launch" },
  { id: "stage6", num: "8", routeNum: "6", name: "Advisory & Structure", shortName: "Advise" },
  { id: "stage7", num: "9", routeNum: "7", name: "Scaling", shortName: "Scale" },
];

const STAGE_ORDER = ALL_STAGES.map(s => s.id);

const getStageStatus = (stageId, currentStage) => {
  const stageIdx = STAGE_ORDER.indexOf(stageId);
  const currentIdx = STAGE_ORDER.indexOf(currentStage);
  if (stageIdx < currentIdx) return 'completed';
  if (stageIdx === currentIdx) return 'current';
  return 'locked';
};

const getCompletionPercent = (currentStage) => {
  const idx = STAGE_ORDER.indexOf(currentStage);
  if (idx < 0) return 0;
  return Math.round((idx / STAGE_ORDER.length) * 100);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    fetchDashboard();
    if (token) {
      axios.get(`${API}/branding`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { if (res.data.branding) setBranding(res.data.branding); })
        .catch(() => {});
    }
  }, []);

  // Branding-aware colors
  const accent = branding?.accentColour || '#F6ADBC';
  const primary = branding?.primaryColour || '#1a1a1a';

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/programme/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
    setLoading(false);
  };

  const completeTask = async (taskId) => {
    try {
      await axios.patch(
        `${API}/tasks/${taskId}`,
        { completed: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDashboard();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-black/40">Loading...</div>
      </div>
    );
  }

  const { stats, todays_tasks } = data || {};

  const quickLinks = [
    { icon: FileText, label: 'Documents', to: '/portal/documents' },
    { icon: AppWindow, label: 'Xero', url: 'https://xero.com', external: true },
    { icon: AppWindow, label: 'My IRD', url: 'https://ird.govt.nz/myir', external: true },
    { icon: CheckSquare, label: 'Tasks', to: '/portal/tasks' },
    { icon: Newspaper, label: 'News', to: '/portal/news' },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-3xl headline text-black mb-2">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-black/60 body-text">
          {user?.business_name ? `Working on ${user.business_name}` : "Let's build your business together."}
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Current Stage */}
        <div className="bg-white rounded-none p-5 border border-border">
          <p className="text-xs text-black/40 uppercase tracking-wide">Current Stage</p>
          <p className="text-2xl headline text-black mt-1">Stage {stats?.current_stage?.num || '0'}</p>
          <p className="text-sm text-black/60 mt-1">{stats?.current_stage?.name || 'Idea Generation'}</p>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-none p-5 border border-border">
          <p className="text-xs text-black/40 uppercase tracking-wide">Progress</p>
          <p className="text-2xl headline text-black mt-1">{stats?.progress_percent || 0}%</p>
          <p className="text-sm text-black/60 mt-1">{stats?.stages_completed || 0} of {stats?.total_stages || 9} stages</p>
        </div>

        {/* Pending Tasks */}
        <div className="bg-white rounded-none p-5 border border-border">
          <p className="text-xs text-black/40 uppercase tracking-wide">Pending Tasks</p>
          <p className="text-2xl headline text-black mt-1">{stats?.pending_tasks || 0}</p>
          <p className="text-sm text-black/60 mt-1">
            {stats?.urgent_tasks > 0 ? `${stats.urgent_tasks} urgent` : 'All on track'}
          </p>
        </div>

        {/* Connected Apps */}
        <div className="bg-white rounded-none p-5 border border-border">
          <p className="text-xs text-black/40 uppercase tracking-wide">Connected Apps</p>
          <p className="text-2xl headline text-black mt-1">{stats?.connected_apps || 0}</p>
          <p className="text-sm text-black/60 mt-1">
            {stats?.apps_need_setup > 0 ? `${stats.apps_need_setup} to set up` : 'All connected'}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Programme Roadmap */}
        <div className="lg:col-span-2 bg-white rounded-none p-6 border border-border" data-testid="progress-roadmap">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg subhead text-black">Your Journey</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/portal/stages')}
              className="text-black/60 hover:text-black"
            >
              View All
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {/* Overall Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-black/60">Overall Progress</span>
              <span className="text-sm font-bold text-black">{getCompletionPercent(stats?.current_stage?.id || 'stage00')}%</span>
            </div>
            <div className="h-3 bg-[#F0F0F0] rounded-none overflow-hidden">
              <div 
                className="h-full rounded-none transition-all duration-700 ease-out"
                style={{ width: `${getCompletionPercent(stats?.current_stage?.id || 'stage00')}%`, background: accent }}
              />
            </div>
          </div>

          {/* Visual Roadmap Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-5 gap-2 sm:gap-3">
            {ALL_STAGES.map((stage) => {
              const status = getStageStatus(stage.id, stats?.current_stage?.id || 'stage00');
              const isCompleted = status === 'completed';
              const isCurrent = status === 'current';
              const isLocked = status === 'locked';

              return (
                <button
                  key={stage.id}
                  onClick={() => {
                    if (isCompleted) navigate(stage.routeNum === '00' ? '/stage1-assessment' : `/stage/${stage.routeNum}`);
                    else if (isCurrent) navigate(stage.routeNum === '00' ? '/stage1-assessment' : `/stage/${stage.routeNum}`);
                  }}
                  disabled={isLocked}
                  className={`
                    relative flex flex-col items-center p-3 rounded-none transition-all duration-200 text-center
                    ${isCompleted ? 'bg-[#d4edda] border-2 border-green-400 hover: cursor-pointer' : ''}
                    ${isLocked ? 'bg-[#F5F5F5] border-2 border-[#E5E5E5] opacity-50 cursor-not-allowed' : ''}
                  `}
                  style={isCurrent ? {
                    background: `${accent}22`, border: `2px solid ${accent}`,
                    boxShadow: `0 4px 12px ${accent}33`, cursor: 'pointer',
                  } : undefined}
                  data-testid={`roadmap-stage-${stage.num}`}
                >
                  {/* Stage circle */}
                  <div
                    className={`
                      w-10 h-10 rounded-none flex items-center justify-center mb-2 font-bold text-sm
                      ${isCompleted ? 'bg-[rgba(255,192,0,0.1)]0 text-white' : ''}
                      ${isLocked ? 'bg-[#E5E5E5] text-[#999]' : ''}
                    `}
                    style={isCurrent ? { background: accent, color: primary } : undefined}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : isLocked ? (
                      <Lock className="h-4 w-4" />
                    ) : (
                      <span>{stage.num}</span>
                    )}
                  </div>
                  {/* Stage name */}
                  <span className={`text-xs font-medium leading-tight ${
                    isCompleted ? 'text-[#1E491A]' :
                    'text-[#999]'
                  }`} style={isCurrent ? { color: primary } : undefined}>
                    {stage.shortName}
                  </span>
                  {/* Current indicator */}
                  {isCurrent && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 rounded-none animate-pulse" style={{ background: accent }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Current Stage CTA */}
          {stats?.current_stage?.id && (
            <div className="mt-4 pt-4 border-t border-[#E5E5E5] flex items-center justify-between">
              <div>
                <p className="text-sm text-black/60">Current Stage</p>
                <p className="font-bold text-black">
                  {ALL_STAGES.find(s => s.id === stats.current_stage.id)?.name || 'Assessment'}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  const stage = ALL_STAGES.find(s => s.id === stats.current_stage.id);
                  if (stage) navigate(`/stage/${stage.routeNum}`);
                }}
                className="rounded-none font-bold"
                style={{ background: accent, color: primary }}
                data-testid="continue-stage-btn"
              >
                Continue
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Quick Access */}
          <div className="bg-white rounded-none p-6 border border-border">
            <h2 className="text-lg subhead text-black mb-4">Quick Access</h2>
            <div className="grid grid-cols-3 gap-3">
              {quickLinks.map((link, idx) => (
                <button
                  key={idx}
                  onClick={() => link.external ? window.open(link.url, '_blank') : navigate(link.to)}
                  className="flex flex-col items-center gap-2 p-3 rounded-none hover:bg-secondary transition-colors"
                >
                  <div className="w-10 h-10 bg-secondary rounded-none flex items-center justify-center">
                    <link.icon className="h-5 w-5 text-black/60" />
                  </div>
                  <span className="text-xs text-black/60">{link.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Today's Tasks */}
          <div className="bg-white rounded-none p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg subhead text-black">Pending Tasks</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/portal/tasks')}
                className="text-black/60 hover:text-black text-xs"
              >
                View All
              </Button>
            </div>
            
            {todays_tasks?.length > 0 ? (
              <div className="space-y-2">
                {todays_tasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-start gap-3 p-3 rounded-none hover:bg-secondary/50 transition-colors"
                  >
                    <button
                      onClick={() => completeTask(task.id)}
                      className="mt-0.5 w-5 h-5 rounded-none border-2 border-border hover:border-primary flex-shrink-0"
                      data-testid={`complete-task-${task.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-black">{task.title}</p>
                      {task.urgent && (
                        <span className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                          <AlertCircle className="h-3 w-3" />
                          Urgent
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-black/40 text-center py-4">
                All caught up!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
